import { useApolloClient } from "@apollo/client";
import { DataGrid, ModalOpenButton } from "@msys/ui";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { CategoriesChips } from "../../categories/CategoriesLabeledValue.js";
import { CategoriesSearchModal } from "../../categories/CategoriesSearchModal.js";
import { ConfirmModal } from "../../../commons/modals/ConfirmModal.js";
import { useDeleteOrganisationQuoteCategoryTextsMutation } from "./QuoteCategoryTexts.generated.js";
import {
  GenericQuoteTexts,
  QuoteCategoryTextsModal,
  QuoteCategoryTextsValue,
} from "./QuoteCategoryTextsModal.js";

interface Props {
  quoteCategoryTexts: QuoteCategoryTextsValue[];
  genericTexts: GenericQuoteTexts;
}

type Editor =
  | { kind: "new"; categoryIds: string[] }
  | { kind: "edit"; set: QuoteCategoryTextsValue };

export const QuoteCategoryTextsSection = ({
  quoteCategoryTexts,
  genericTexts,
}: Props) => {
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const [deleteTexts] = useDeleteOrganisationQuoteCategoryTextsMutation({
    client,
  });
  const [editor, setEditor] = React.useState<Editor | null>(null);

  const usedCategoryIds = React.useMemo(
    () => quoteCategoryTexts.flatMap(set => set.categoryIds),
    [quoteCategoryTexts]
  );

  const columns = React.useMemo(
    () => [
      {
        field: "categoryIds",
        headerName: t("Categories", { ns: "Global" }),
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: ({ row }: { row: QuoteCategoryTextsValue }) => (
          <Box sx={{ py: 1 }}>
            <CategoriesChips
              categoryIds={row.categoryIds}
              removeChildrenIfIncluded
              addParentsWhenNotIncluded={false}
            />
          </Box>
        ),
      },
      {
        field: "opening",
        headerName: t("Opening text", { ns: "OrganisationSettings" }),
        width: 110,
        sortable: false,
        renderCell: ({ row }: { row: QuoteCategoryTextsValue }) => (
          <Typography variant="body2">
            {row.opening == null
              ? t("General", { ns: "OrganisationSettings" })
              : t("Custom", { ns: "OrganisationSettings" })}
          </Typography>
        ),
      },
      {
        field: "closing",
        headerName: t("Closing text", { ns: "OrganisationSettings" }),
        width: 110,
        sortable: false,
        renderCell: ({ row }: { row: QuoteCategoryTextsValue }) => (
          <Typography variant="body2">
            {row.closing == null
              ? t("General", { ns: "OrganisationSettings" })
              : t("Custom", { ns: "OrganisationSettings" })}
          </Typography>
        ),
      },
      {
        field: "actions",
        headerName: "",
        flex: 0,
        width: 92,
        sortable: false,
        renderCell: ({ row }: { row: QuoteCategoryTextsValue }) => (
          <Stack direction="row" spacing={0.5}>
            <IconButton
              color="primary"
              size="small"
              aria-label={t("Edit", { ns: "Global" })}
              onClick={() => setEditor({ kind: "edit", set: row })}
            >
              <EditIcon />
            </IconButton>
            <ModalOpenButton
              Modal={ConfirmModal}
              modalProps={{
                text: t(
                  "Delete these category-specific texts? Quotes in these categories will use the general texts.",
                  { ns: "OrganisationSettings" }
                ),
                handleConfirm: async () => {
                  await deleteTexts({ variables: { input: { id: row.id } } });
                  enqueueSnackbar(t("Changes saved", { ns: "Global" }), {
                    variant: "success",
                  });
                },
              }}
            >
              <IconButton
                color="primary"
                size="small"
                aria-label={t("Delete", { ns: "Global" })}
              >
                <DeleteIcon />
              </IconButton>
            </ModalOpenButton>
          </Stack>
        ),
      },
    ],
    [t, deleteTexts, enqueueSnackbar]
  );

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <Typography variant="h3">
          {t("Category-specific texts", { ns: "OrganisationSettings" })}
        </Typography>
        <ModalOpenButton
          Modal={CategoriesSearchModal}
          modalProps={{
            excludeCategoryIds: usedCategoryIds,
            handleSelectCategories: (categories, handleClose) => {
              setEditor({
                kind: "new",
                categoryIds: categories.map(category => category.id),
              });
              handleClose();
            },
          }}
        >
          <Button size="small" color="secondary" startIcon={<AddIcon />}>
            {t("Add", { ns: "Global" })}
          </Button>
        </ModalOpenButton>
      </Stack>
      <Typography variant="body2" color="secondary">
        {t("Use different intro and closing texts for specific categories.", {
          ns: "OrganisationSettings",
        })}
      </Typography>
      <DataGrid
        columns={columns}
        rows={quoteCategoryTexts}
        hideFooter
        paginationMode="client"
        disableColumnMenu
        disableColumnResize
        disableRowSelectionOnClick
        autoHeight
        getRowHeight={() => "auto"}
        localeText={{
          noRowsLabel: t(
            "No category-specific texts yet. The general texts above are used.",
            { ns: "OrganisationSettings" }
          ),
        }}
        sx={{ "& .MuiDataGrid-row.MuiDataGrid-row": { cursor: "default" } }}
      />
      {editor ? (
        <QuoteCategoryTextsModal
          handleClose={() => setEditor(null)}
          quoteCategoryTexts={editor.kind === "edit" ? editor.set : null}
          initialCategoryIds={editor.kind === "new" ? editor.categoryIds : []}
          genericTexts={genericTexts}
          allQuoteCategoryTexts={quoteCategoryTexts}
        />
      ) : null}
    </Stack>
  );
};
